import { useCallback } from "react";
import { useParams } from "react-router-dom";

import { useMutation } from "@apollo/client";
import { type LensCompoundId } from "../../../gql-generated/types";
import { GroupCache } from "../../groups/api/cache";
import { getGroupDetails } from "../../groups/api/queries/getGroupV2";
import { LensCache } from "../../lenses/api/cache";
import { LensCompoundIdOperations } from "../../lenses/models";
import { releaseConnectionBulk } from "../api/mutation/releaseConnectionBulk";
import {
    type ReleaseConnectionBulkMutation,
    type ReleaseConnectionBulkMutationVariables,
} from "../api/mutation/releaseConnectionBulk.gql-generated";

export function useRemoveConnections() {
    const { orgId } = useParams();
    const [releaseConnections, { loading, error }] = useMutation<
        ReleaseConnectionBulkMutation,
        ReleaseConnectionBulkMutationVariables
    >(releaseConnectionBulk, {
        update: (cache, { data }) => {
            data?.releaseConnectionBulk?.releaseResponses?.forEach((org) => {
                const { connectionId, lensCompoundId, groupId: removedConnectionGroupId } = org ?? {};
                LensCache.removeConnection(cache, lensCompoundId as LensCompoundId, connectionId as string);
                GroupCache.removeConnection(cache, removedConnectionGroupId as string, connectionId as string);
            });
        },
    });

    const removeGroupConnections = useCallback(
        (groupId: string, lensIds: readonly LensCompoundId[]) =>
            lensIds.length === 0
                ? Promise.resolve()
                : releaseConnections({
                      variables: {
                          releaseRequests: lensIds.map((lensId) => ({
                              groupId,
                              organizationId: orgId as string,
                              lensId: undefined,
                              lensCompoundId: lensId && LensCompoundIdOperations.toVariables(lensId),
                          })),
                      },
                      refetchQueries: [
                          { query: getGroupDetails, variables: { id: groupId, organizationId: orgId as string } },
                      ],
                  }),
        [releaseConnections, orgId],
    );

    const removeLensConnections = useCallback(
        (lensCompoundId: LensCompoundId, groupIds: readonly string[]) =>
            groupIds.length === 0
                ? Promise.resolve()
                : releaseConnections({
                      variables: {
                          releaseRequests: groupIds.map((groupId) => ({
                              groupId,
                              organizationId: orgId as string,
                              lensCompoundId,
                              lensId: undefined,
                          })),
                      },
                  }),
        [releaseConnections, orgId],
    );

    return {
        removeGroupConnections,
        removeLensConnections,
        loading,
        error,
    };
}
